import { QUERY_KEYS } from "@/common/query-keys";
import { updateAssetLocation } from "@/lib/snipeit/hardware";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const CONCURRENCY_LIMIT = 5;

export interface MoveResult {
  fulfilled: number[];
  rejected: number[];
}

export function useMoveAssets({
  onMutate,
  onSuccess,
  onSettled,
}: {
  onMutate?: (ids: number[]) => void;
  onSuccess?: (result: MoveResult) => void;
  onSettled?: () => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, targetId }: { ids: number[]; targetId: number }): Promise<MoveResult> => {
      const fulfilled: number[] = [];
      const rejected: number[] = [];
      for (let i = 0; i < ids.length; i += CONCURRENCY_LIMIT) {
        const batch = ids.slice(i, i + CONCURRENCY_LIMIT);
        const results = await Promise.allSettled(batch.map((id) => updateAssetLocation(id, targetId)));
        for (let j = 0; j < batch.length; j++) {
          if (results[j].status === "fulfilled") {
            fulfilled.push(batch[j]);
          } else {
            rejected.push(batch[j]);
          }
        }
      }
      return { fulfilled, rejected };
    },
    onMutate: ({ ids }) => onMutate?.(ids),
    onSuccess: (result) => {
      onSuccess?.(result);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.locationAssetsPage] });
    },
    onSettled,
  });
}
